// A destroyed enemy Monarch's trophy (§31 A2): take one of three cards, or keep
// it for score. The kit's `Modal`, over `SessionView.hand.trophies`.
//
// **"Later" hides it, it does not decline it.** The trophy stays in the run
// until claimed or kept; a new one reopens the chooser. Typed on `src/api`.

import { useState } from "react";

import type { OptionId, TrophyView } from "src/api";
import {
  Btn,
  CardArt,
  CardPlate,
  CardText,
  GameCard,
  Icon,
  Modal,
  ModalActions,
  ModalBody,
  ModalTitle,
  styled
} from "./kit";

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.kit.space.md,
  justifyContent: "center",
  flexWrap: "wrap"
}));

const Face = styled(GameCard)({ display: "flex", flexDirection: "column" });

export function TrophyChooser({
  trophies,
  onChoose
}: {
  trophies: readonly TrophyView[] | undefined;
  onChoose: (option: OptionId) => void;
}) {
  const [later, setLater] = useState<number | null>(null);
  const trophy = trophies?.find((one) => later === null || one.id > later);
  if (!trophy) return null;

  return (
    <Modal open onClose={() => setLater(trophy.id)} maxWidth="md" data-testid="trophy-chooser">
      <ModalTitle>A Monarch falls — take a trophy</ModalTitle>
      <ModalBody>
        <Row>
          {trophy.offer.map((card, i) => (
            <Face
              key={card.option}
              rarity={card.rarity}
              role="button"
              tabIndex={0}
              data-testid={`trophy-card-${i}`}
              onClick={() => onChoose(card.option)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onChoose(card.option);
              }}
            >
              <CardPlate>{card.name}</CardPlate>
              <CardArt>
                {card.glyph.kind === "icon" ? <Icon name={card.glyph.name} size={40} /> : card.glyph.text}
              </CardArt>
              <CardText>{card.short}</CardText>
            </Face>
          ))}
        </Row>
      </ModalBody>
      <ModalActions>
        <Btn variant="ghost" data-testid="trophy-later" onClick={() => setLater(trophy.id)}>
          Later
        </Btn>
        <Btn variant="primary" data-testid="trophy-keep" onClick={() => onChoose(trophy.keep)}>
          Keep it (+{trophy.score} score)
        </Btn>
      </ModalActions>
    </Modal>
  );
}
